import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaClipboardList,
  FaEnvelope,
  FaFlask,
  FaHospitalUser,
  FaPaperPlane,
  FaSignOutAlt,
  FaXRay,
} from "react-icons/fa";
import { supabase } from "../supabaseClient";

const REFERRAL_PRIORITIES = ["Routine", "Urgent", "Emergency"];
const TEST_PRIORITIES = ["Routine", "Urgent", "Emergency"];
const IMAGING_TEST_TYPES = new Set(["X-ray", "CT", "MRI", "Ultrasound"]);
const IMAGING_BODY_PARTS = ["Random", "Head", "Chest", "Shoulder", "Elbow", "Forearm", "Wrist", "Hand", "Knee", "Lower leg", "Ankle"];
const IMAGING_LATERALITIES = ["Random", "Left", "Right"];
const TEST_TYPES = [
  "Blood test",
  "X-ray",
  "CT",
  "MRI",
  "ECG",
  "Ultrasound",
  "Urinalysis",
  "Other",
];

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function trimOrNull(value) {
  const text = String(value || "").trim();
  return text || null;
}

export default function ClinicalPatientWorkspace({
  patient,
  portalProfile,
  currentUser,
  onPatientDischarged,
}) {
  const patientId = patient?.id;
  const portalAccountId = patient?.portal_account_id || portalProfile?.id || null;

  const [activeTab, setActiveTab] = useState("timeline");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [records, setRecords] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [tests, setTests] = useState([]);
  const [radiologyStudies, setRadiologyStudies] = useState([]);
  const [discharges, setDischarges] = useState([]);
  const [messages, setMessages] = useState([]);
  const [bedMovements, setBedMovements] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [observations, setObservations] = useState([]);
  const [structuredAlerts, setStructuredAlerts] = useState([]);
  const [medicationOrders, setMedicationOrders] = useState([]);
  const [medicationAdministrations, setMedicationAdministrations] = useState([]);
  const [clinicalTasks, setClinicalTasks] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [referralDraft, setReferralDraft] = useState({
    referred_to: "",
    priority: "Routine",
    reason: "",
  });
  const [testDraft, setTestDraft] = useState({
    test_type: "Blood test",
    priority: "Routine",
    clinical_details: "",
    body_part: "Random",
    laterality: "Random",
  });
  const [messageDraft, setMessageDraft] = useState({
    subject: "",
    category: "General",
    body: "",
  });
  const [dischargeDraft, setDischargeDraft] = useState({
    diagnosis: "",
    treatment: "",
    medications: "",
    discharge_advice: "",
    follow_up: "",
  });
  const [working, setWorking] = useState(false);

  const loadClinicalData = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    setError("");

    let appointmentQuery = supabase
      .from("appointments")
      .select("id, patient_id, portal_account_id, department, appointment_datetime, location, appointment_status, status, checked_in_at");

    if (portalAccountId) {
      appointmentQuery = appointmentQuery.or(
        `patient_id.eq.${patientId},portal_account_id.eq.${portalAccountId}`,
      );
    } else {
      appointmentQuery = appointmentQuery.eq("patient_id", patientId);
    }

    const [
      recordResult,
      referralResult,
      testResult,
      radiologyResult,
      dischargeResult,
      bedResult,
      appointmentResult,
      observationResult,
      structuredAlertResult,
      medicationOrderResult,
      medicationAdministrationResult,
      taskResult,
      handoverResult,
      documentResult,
    ] = await Promise.all([
        supabase
          .from("patient_records")
          .select("id, entry, created_at")
          .eq("patient_id", patientId)
          .order("created_at", { ascending: false }),
        supabase
          .from("clinical_referrals")
          .select("*")
          .eq("patient_id", patientId)
          .order("created_at", { ascending: false }),
        supabase
          .from("diagnostic_requests")
          .select("*")
          .eq("patient_id", patientId)
          .order("requested_at", { ascending: false }),
        supabase
          .from("radiology_studies")
          .select("id, diagnostic_request_id, modality, body_part, laterality, projection, generation_status, report_status, study_url, generated_at")
          .eq("patient_id", patientId)
          .order("created_at", { ascending: false }),
        supabase
          .from("discharge_summaries")
          .select("*")
          .eq("patient_id", patientId)
          .order("discharged_at", { ascending: false }),
        supabase
          .from("bed_movements")
          .select("*")
          .eq("patient_id", patientId)
          .order("moved_at", { ascending: false }),
        appointmentQuery.order("appointment_datetime", { ascending: false }),
        supabase.from("clinical_observations").select("id, recorded_at, heart_rate, systolic_bp, diastolic_bp, respiratory_rate, oxygen_saturation, temperature_c, consciousness").eq("patient_id", patientId).order("recorded_at", { ascending: false }),
        supabase.from("clinical_alerts").select("id, title, alert_type, severity, active, created_at, resolved_at").eq("patient_id", patientId).order("created_at", { ascending: false }),
        supabase.from("medication_orders").select("id, medication, dose, route, status, prescribed_at, stopped_at").eq("patient_id", patientId).order("prescribed_at", { ascending: false }),
        supabase.from("medication_administrations").select("id, order_id, administration_status, administered_at, medication_orders(medication, dose)").eq("patient_id", patientId).order("administered_at", { ascending: false }),
        supabase.from("clinical_tasks").select("id, title, priority, status, created_at, completed_at").eq("patient_id", patientId).order("created_at", { ascending: false }),
        supabase.from("handover_entries").select("id, current_problem, plan, created_at, archived").eq("patient_id", patientId).order("created_at", { ascending: false }),
        supabase.from("clinical_documents").select("id, title, document_type, patient_visible, created_at").eq("patient_id", patientId).order("created_at", { ascending: false }),
      ]);

    const firstError = [
      recordResult,
      referralResult,
      testResult,
      radiologyResult,
      dischargeResult,
      bedResult,
      appointmentResult,
      observationResult,
      structuredAlertResult,
      medicationOrderResult,
      medicationAdministrationResult,
      taskResult,
      handoverResult,
      documentResult,
    ].find((result) => result.error)?.error;

    if (firstError) {
      console.error("Unable to load clinical workflow data:", firstError);
      setError(
        "Some clinical workflow data could not be loaded. Make sure the required eCAMIS migrations, including ADD_ECAMIS_AI_RADIOLOGY.sql, have been run.",
      );
    }

    setRecords(recordResult.data || []);
    setReferrals(referralResult.data || []);
    setTests(testResult.data || []);
    setRadiologyStudies(radiologyResult.data || []);
    setDischarges(dischargeResult.data || []);
    setBedMovements(bedResult.data || []);
    setAppointments(appointmentResult.data || []);
    setObservations(observationResult.data || []);
    setStructuredAlerts(structuredAlertResult.data || []);
    setMedicationOrders(medicationOrderResult.data || []);
    setMedicationAdministrations(medicationAdministrationResult.data || []);
    setClinicalTasks(taskResult.data || []);
    setHandovers(handoverResult.data || []);
    setDocuments(documentResult.data || []);

    if (portalAccountId) {
      const messageResult = await supabase
        .from("patient_messages")
        .select("*")
        .eq("portal_account_id", portalAccountId)
        .order("created_at", { ascending: false });

      if (messageResult.error) {
        console.error("Unable to load patient messages:", messageResult.error);
      }
      setMessages(messageResult.data || []);
    } else {
      setMessages([]);
    }

    setLoading(false);
  }, [patientId, portalAccountId]);

  useEffect(() => {
    loadClinicalData();
  }, [loadClinicalData]);

  const timeline = useMemo(() => {
    const items = [];

    records.forEach((row) => {
      const entry = String(row.entry || "");
      const movementEntry = entry.startsWith("[Movement]");
      items.push({
        id: `record-${row.id}`,
        at: row.created_at,
        type: movementEntry ? "Patient movement" : "Clinical entry",
        title: movementEntry ? "Patient location changed" : "Clinical record entry added",
        detail: movementEntry
          ? entry.replace(/^\[Movement\]\s*/, "").split("\n").filter(Boolean).join(" · ")
          : entry.split("\n")[0] || "Clinical entry",
      });
    });

    referrals.forEach((row) =>
      items.push({
        id: `referral-${row.id}`,
        at: row.created_at,
        type: "Referral",
        title: `${row.priority || "Routine"} referral to ${row.referred_to}`,
        detail: `${row.status || "Submitted"} · ${row.reason || "No reason recorded"}`,
      }),
    );

    tests.forEach((row) =>
      items.push({
        id: `test-${row.id}`,
        at: row.resulted_at || row.requested_at,
        type: "Diagnostics",
        title: `${row.test_type} · ${row.status || "Requested"}`,
        detail: row.result_summary || row.clinical_details || "No additional details",
      }),
    );

    discharges.forEach((row) =>
      items.push({
        id: `discharge-${row.id}`,
        at: row.discharged_at,
        type: "Discharge",
        title: `Discharged · ${row.diagnosis}`,
        detail: row.follow_up || row.discharge_advice || "Discharge summary recorded",
      }),
    );

    appointments.forEach((row) => {
      const cancelled =
        row.status === false ||
        String(row.appointment_status || "").toLowerCase() === "cancelled";
      items.push({
        id: `appointment-${row.id}`,
        at: row.checked_in_at || row.appointment_datetime,
        type: "Appointment",
        title: cancelled
          ? `${row.department || "Appointment"} cancelled`
          : row.checked_in_at
            ? `${row.department || "Appointment"} checked in`
            : `${row.department || "Appointment"} scheduled`,
        detail: `${row.location || "RUHT"} · ${formatDateTime(row.appointment_datetime)}`,
      });
    });

    bedMovements.forEach((row) =>
      items.push({
        id: `bed-${row.id}`,
        at: row.moved_at,
        type: "Bed movement",
        title: row.to_bed
          ? `Moved to ${row.to_ward || "ward"} · ${row.to_bed}`
          : "Bed released",
        detail: row.from_bed
          ? `Previous: ${row.from_ward || "ward"} · ${row.from_bed}`
          : "No previous bed recorded",
      }),
    );

    messages.forEach((row) =>
      items.push({
        id: `message-${row.id}`,
        at: row.created_at,
        type: "Portal message",
        title: row.subject,
        detail: row.body,
      }),
    );

    observations.forEach((row) =>
      items.push({
        id: `observation-${row.id}`,
        at: row.recorded_at,
        type: "Observations",
        title: "Observations recorded",
        detail: `HR ${row.heart_rate ?? "–"} · BP ${row.systolic_bp ?? "–"}/${row.diastolic_bp ?? "–"} · RR ${row.respiratory_rate ?? "–"} · SpO₂ ${row.oxygen_saturation ?? "–"}% · Temp ${row.temperature_c ?? "–"}°C`,
      }),
    );

    structuredAlerts.forEach((row) =>
      items.push({
        id: `structured-alert-${row.id}`,
        at: row.resolved_at || row.created_at,
        type: "Clinical alert",
        title: row.active === false ? `${row.title} resolved` : row.title,
        detail: `${row.severity || "Information"} · ${row.alert_type || "Alert"}`,
      }),
    );

    medicationOrders.forEach((row) =>
      items.push({
        id: `med-order-${row.id}`,
        at: row.stopped_at || row.prescribed_at,
        type: "Medication",
        title: `${row.medication} · ${row.dose}`,
        detail: `${row.status || "Active"}${row.route ? ` · ${row.route}` : ""}`,
      }),
    );

    medicationAdministrations.forEach((row) =>
      items.push({
        id: `med-admin-${row.id}`,
        at: row.administered_at,
        type: "Medication administration",
        title: `${row.medication_orders?.medication || "Medication"} · ${row.administration_status}`,
        detail: row.medication_orders?.dose || "Administration recorded",
      }),
    );

    clinicalTasks.forEach((row) =>
      items.push({
        id: `task-${row.id}`,
        at: row.completed_at || row.created_at,
        type: "Clinical task",
        title: row.status === "Completed" ? `${row.title} completed` : row.title,
        detail: `${row.priority || "Routine"} · ${row.status || "Open"}`,
      }),
    );

    handovers.forEach((row) =>
      items.push({
        id: `handover-${row.id}`,
        at: row.created_at,
        type: "Handover",
        title: row.current_problem,
        detail: row.plan || "Handover entry recorded",
      }),
    );

    documents.forEach((row) =>
      items.push({
        id: `document-${row.id}`,
        at: row.created_at,
        type: "Document",
        title: row.title,
        detail: `${row.document_type}${row.patient_visible ? " · Shared with patient" : ""}`,
      }),
    );

    return items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [appointments, bedMovements, clinicalTasks, discharges, documents, handovers, medicationAdministrations, medicationOrders, messages, observations, records, referrals, structuredAlerts, tests]);

  async function createReferral(event) {
    event.preventDefault();
    if (!patientId || !referralDraft.referred_to.trim() || !referralDraft.reason.trim()) return;

    setWorking(true);
    setError("");
    const { error: insertError } = await supabase.from("clinical_referrals").insert({
      patient_id: patientId,
      portal_account_id: portalAccountId,
      referred_to: referralDraft.referred_to.trim(),
      priority: referralDraft.priority,
      reason: referralDraft.reason.trim(),
      status: "Submitted",
      created_by: currentUser?.id || null,
    });

    if (insertError) {
      console.error(insertError);
      setError(insertError.message || "The referral could not be created.");
    } else {
      setReferralDraft({ referred_to: "", priority: "Routine", reason: "" });
      setMessage("Referral submitted to the clinical worklist.");
      await loadClinicalData();
    }
    setWorking(false);
  }

  async function createTest(event) {
    event.preventDefault();
    if (!patientId || !testDraft.test_type.trim()) return;

    setWorking(true);
    setError("");
    setMessage("");

    if (IMAGING_TEST_TYPES.has(testDraft.test_type)) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (!token) throw new Error("Your session has expired. Please sign in again.");

        const response = await fetch("/api/radiology", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "generate",
            patientId,
            portalAccountId,
            testType: testDraft.test_type,
            priority: testDraft.priority,
            clinicalDetails: trimOrNull(testDraft.clinical_details),
            bodyPart: testDraft.body_part,
            laterality: testDraft.laterality,
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "The AI imaging study could not be generated.");

        setTestDraft({ test_type: "Blood test", priority: "Routine", clinical_details: "", body_part: "Random", laterality: "Random" });
        setMessage("AI Roblox imaging generated and added to the radiology worklist for clinician reporting.");
        await loadClinicalData();
      } catch (generationError) {
        console.error(generationError);
        setError(generationError.message || "The AI imaging study could not be generated.");
      } finally {
        setWorking(false);
      }
      return;
    }

    const { error: insertError } = await supabase.from("diagnostic_requests").insert({
      patient_id: patientId,
      portal_account_id: portalAccountId,
      test_type: testDraft.test_type.trim(),
      priority: testDraft.priority,
      clinical_details: trimOrNull(testDraft.clinical_details),
      status: "Requested",
      requested_by: currentUser?.id || null,
    });

    if (insertError) {
      console.error(insertError);
      setError(insertError.message || "The test request could not be created.");
    } else {
      setTestDraft({ test_type: "Blood test", priority: "Routine", clinical_details: "", body_part: "Random", laterality: "Random" });
      setMessage("Diagnostic request added to the clinical worklist.");
      await loadClinicalData();
    }
    setWorking(false);
  }

  async function sendPatientMessage(event) {
    event.preventDefault();
    if (!portalAccountId) {
      setError("This patient is not linked to a patient portal account.");
      return;
    }

    if (!messageDraft.subject.trim() || !messageDraft.body.trim()) return;

    setWorking(true);
    setError("");
    const { error: insertError } = await supabase.from("patient_messages").insert({
      portal_account_id: portalAccountId,
      patient_id: patientId,
      subject: messageDraft.subject.trim(),
      body: messageDraft.body.trim(),
      category: messageDraft.category,
      created_by: currentUser?.id || null,
      action_url: "/patient-portal",
    });

    if (insertError) {
      console.error(insertError);
      setError(insertError.message || "The portal message could not be sent.");
    } else {
      setMessageDraft({ subject: "", category: "General", body: "" });
      setMessage("Message sent to the patient portal inbox.");
      await loadClinicalData();
    }
    setWorking(false);
  }

  async function createDischargeSummary(event) {
    event.preventDefault();
    if (!dischargeDraft.diagnosis.trim()) return;

    setWorking(true);
    setError("");

    const { data: summary, error: insertError } = await supabase
      .from("discharge_summaries")
      .insert({
        patient_id: patientId,
        portal_account_id: portalAccountId,
        diagnosis: dischargeDraft.diagnosis.trim(),
        treatment: trimOrNull(dischargeDraft.treatment),
        medications: trimOrNull(dischargeDraft.medications),
        discharge_advice: trimOrNull(dischargeDraft.discharge_advice),
        follow_up: trimOrNull(dischargeDraft.follow_up),
        discharged_by: currentUser?.id || null,
      })
      .select("id, discharged_at")
      .single();

    if (insertError) {
      console.error(insertError);
      setError(insertError.message || "The discharge summary could not be saved.");
      setWorking(false);
      return;
    }

    const { data: occupiedBeds } = await supabase
      .from("beds")
      .select("id, ward, bed_number")
      .eq("patient_id", patientId);

    if (occupiedBeds?.length) {
      await Promise.all(
        occupiedBeds.map((bed) =>
          supabase.from("bed_movements").insert({
            patient_id: patientId,
            from_ward: bed.ward || null,
            from_bed: bed.bed_number ? `Bed ${bed.bed_number}` : null,
            to_ward: null,
            to_bed: null,
            moved_by: currentUser?.id || null,
            notes: "Bed released on discharge",
          }),
        ),
      );

      await supabase
        .from("beds")
        .update({ patient_id: null, status: "Available" })
        .eq("patient_id", patientId);
    }

    await supabase
      .from("patients")
      .update({ archived: true, bed: null })
      .eq("id", patientId);

    if (portalAccountId) {
      const dischargeLetter = [
        `Diagnosis / outcome: ${dischargeDraft.diagnosis.trim()}`,
        dischargeDraft.treatment.trim() ? `Treatment: ${dischargeDraft.treatment.trim()}` : "",
        dischargeDraft.medications.trim() ? `Medication on discharge: ${dischargeDraft.medications.trim()}` : "",
        dischargeDraft.discharge_advice.trim() ? `Discharge advice: ${dischargeDraft.discharge_advice.trim()}` : "",
        dischargeDraft.follow_up.trim() ? `Follow-up: ${dischargeDraft.follow_up.trim()}` : "",
      ].filter(Boolean).join("\n\n");

      await supabase.from("patient_messages").insert({
        portal_account_id: portalAccountId,
        patient_id: patientId,
        subject: "Your discharge summary is available",
        category: "Discharge",
        body: `Your RUHT visit has been marked as discharged. Diagnosis: ${dischargeDraft.diagnosis.trim()}${
          dischargeDraft.follow_up.trim()
            ? `\n\nFollow-up: ${dischargeDraft.follow_up.trim()}`
            : ""
        }`,
        created_by: currentUser?.id || null,
        action_url: "/patient-portal",
      });

      const documentResult = await supabase.from("clinical_documents").insert({
        patient_id: patientId,
        portal_account_id: portalAccountId,
        document_type: "Discharge letter",
        title: `Discharge summary · ${dischargeDraft.diagnosis.trim()}`,
        content: dischargeLetter,
        patient_visible: true,
        created_by: currentUser?.id || null,
      });

      if (documentResult.error) {
        console.warn(
          "The discharge summary was saved, but the patient document could not be created:",
          documentResult.error,
        );
      }
    }

    setDischargeDraft({
      diagnosis: "",
      treatment: "",
      medications: "",
      discharge_advice: "",
      follow_up: "",
    });
    setMessage(`Discharge summary saved ${summary?.discharged_at ? `at ${formatDateTime(summary.discharged_at)}` : ""}.`);
    await loadClinicalData();
    onPatientDischarged?.();
    setWorking(false);
  }

  const radiologyByDiagnosticId = useMemo(() => {
    const map = new Map();
    radiologyStudies.forEach((study) => map.set(study.diagnostic_request_id, study));
    return map;
  }, [radiologyStudies]);

  const isImagingRequest = IMAGING_TEST_TYPES.has(testDraft.test_type);

  const tabs = [
    ["timeline", "Timeline", FaClipboardList],
    ["referrals", "Referrals", FaPaperPlane],
    ["tests", "Tests & results", FaFlask],
    ["messages", "Patient inbox", FaEnvelope],
    ["discharge", "Discharge", FaSignOutAlt],
  ];

  return (
    <section className="pguh-clinical-workspace">
      <div className="pguh-clinical-workspace__header">
        <div>
          <span className="pguh-eyebrow">Integrated clinical workflow</span>
          <h2 className="nhsuk-heading-m">Clinical workspace</h2>
        </div>
        <button
          type="button"
          className="nhsuk-button nhsuk-button--secondary nhsuk-u-margin-0"
          onClick={loadClinicalData}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="pguh-clinical-tabs" role="tablist" aria-label="Clinical workspace sections">
        {tabs.map(([value, label, Icon]) => (
          <button
            key={value}
            type="button"
            className={activeTab === value ? "is-active" : ""}
            onClick={() => setActiveTab(value)}
          >
            <Icon aria-hidden="true" /> {label}
          </button>
        ))}
      </div>

      {message && <div className="pguh-timesheet-message pguh-timesheet-message--success">{message}</div>}
      {error && <div className="pguh-timesheet-message pguh-timesheet-message--error">{error}</div>}

      {activeTab === "timeline" && (
        <div className="pguh-clinical-timeline">
          {timeline.length === 0 ? (
            <div className="pguh-empty-state">
              <FaHospitalUser aria-hidden="true" />
              <h3>No timeline events yet</h3>
              <p>Clinical events will appear here as the patient moves through care.</p>
            </div>
          ) : (
            timeline.map((item) => (
              <article key={item.id} className="pguh-clinical-timeline__item">
                <div className="pguh-clinical-timeline__marker" />
                <div>
                  <span>{item.type} · {formatDateTime(item.at)}</span>
                  <h3>{item.title}</h3>
                  <p>{item.detail}</p>
                </div>
              </article>
            ))
          )}
        </div>
      )}

      {activeTab === "referrals" && (
        <div className="pguh-clinical-two-column">
          <form className="pguh-clinical-form" onSubmit={createReferral}>
            <h3 className="nhsuk-heading-s">New referral</h3>
            <label className="nhsuk-label" htmlFor="referral-destination">Refer to</label>
            <input
              id="referral-destination"
              className="nhsuk-input"
              placeholder="e.g. Cardiology"
              value={referralDraft.referred_to}
              onChange={(event) => setReferralDraft((current) => ({ ...current, referred_to: event.target.value }))}
              required
            />
            <label className="nhsuk-label" htmlFor="referral-priority">Priority</label>
            <select
              id="referral-priority"
              className="nhsuk-select"
              value={referralDraft.priority}
              onChange={(event) => setReferralDraft((current) => ({ ...current, priority: event.target.value }))}
            >
              {REFERRAL_PRIORITIES.map((priority) => <option key={priority}>{priority}</option>)}
            </select>
            <label className="nhsuk-label" htmlFor="referral-reason">Clinical reason</label>
            <textarea
              id="referral-reason"
              className="nhsuk-textarea"
              rows={5}
              value={referralDraft.reason}
              onChange={(event) => setReferralDraft((current) => ({ ...current, reason: event.target.value }))}
              required
            />
            <button className="nhsuk-button" disabled={working}>Submit referral</button>
          </form>

          <div className="pguh-clinical-list">
            <h3 className="nhsuk-heading-s">Referral history</h3>
            {referrals.length === 0 ? <p>No referrals recorded.</p> : referrals.map((row) => (
              <article key={row.id}>
                <div><strong>{row.referred_to}</strong><span className={`pguh-clinical-priority pguh-clinical-priority--${String(row.priority).toLowerCase()}`}>{row.priority}</span></div>
                <p>{row.reason}</p>
                <small>{row.status} · {formatDateTime(row.created_at)}</small>
                {row.response_notes && <p><strong>Response:</strong> {row.response_notes}</p>}
              </article>
            ))}
          </div>
        </div>
      )}

      {activeTab === "tests" && (
        <div className="pguh-clinical-two-column">
          <form className="pguh-clinical-form" onSubmit={createTest}>
            <h3 className="nhsuk-heading-s">Request a test</h3>
            <label className="nhsuk-label" htmlFor="test-type">Test</label>
            <select id="test-type" className="nhsuk-select" value={testDraft.test_type} onChange={(event) => setTestDraft((current) => ({ ...current, test_type: event.target.value }))}>
              {TEST_TYPES.map((type) => <option key={type}>{type}</option>)}
            </select>
            <label className="nhsuk-label" htmlFor="test-priority">Priority</label>
            <select id="test-priority" className="nhsuk-select" value={testDraft.priority} onChange={(event) => setTestDraft((current) => ({ ...current, priority: event.target.value }))}>
              {TEST_PRIORITIES.map((priority) => <option key={priority}>{priority}</option>)}
            </select>

            {isImagingRequest && (
              <div className="ruht-imaging-request-options">
                <div className="ruht-imaging-request-options__heading"><FaXRay aria-hidden="true" /><div><strong>AI Roblox imaging</strong><span>A different hidden roleplay case is generated for each study.</span></div></div>
                <label className="nhsuk-label" htmlFor="imaging-body-part">Body area</label>
                <select id="imaging-body-part" className="nhsuk-select" value={testDraft.body_part} onChange={(event) => setTestDraft((current) => ({ ...current, body_part: event.target.value }))}>
                  {IMAGING_BODY_PARTS.map((part) => <option key={part}>{part}</option>)}
                </select>
                <label className="nhsuk-label" htmlFor="imaging-laterality">Side</label>
                <select id="imaging-laterality" className="nhsuk-select" value={testDraft.laterality} onChange={(event) => setTestDraft((current) => ({ ...current, laterality: event.target.value }))}>
                  {IMAGING_LATERALITIES.map((side) => <option key={side}>{side}</option>)}
                </select>
                <p className="nhsuk-hint">Each study starts from the exact R6 head, torso, arm or leg shape from the supplied Roblox reference. Random can produce normal studies, fractures, dislocations, healing injuries or soft-tissue/sprain-style cases. The hidden case answer is not shown to the reporting clinician.</p>
              </div>
            )}

            <label className="nhsuk-label" htmlFor="test-details">Clinical details</label>
            <textarea id="test-details" className="nhsuk-textarea" rows={5} value={testDraft.clinical_details} onChange={(event) => setTestDraft((current) => ({ ...current, clinical_details: event.target.value }))} />
            <button className="nhsuk-button" disabled={working}>
              {working ? (isImagingRequest ? "Generating imaging…" : "Requesting…") : (isImagingRequest ? "Generate AI imaging request" : "Request test")}
            </button>
          </form>

          <div className="pguh-clinical-list">
            <h3 className="nhsuk-heading-s">Tests and results</h3>
            {tests.length === 0 ? <p>No tests requested.</p> : tests.map((row) => {
              const imaging = radiologyByDiagnosticId.get(row.id);
              return (
                <article key={row.id} className={imaging ? "ruht-imaging-result-card" : ""}>
                  <div><strong>{row.test_type}</strong><span className={`pguh-result-flag pguh-result-flag--${String(row.result_flag || "pending").toLowerCase()}`}>{row.result_flag || row.status}</span></div>
                  <p>{row.result_summary || row.clinical_details || "No details recorded."}</p>
                  <small>{row.status} · requested {formatDateTime(row.requested_at)}</small>
                  {imaging && (
                    <div className="ruht-imaging-result-card__actions">
                      <span><FaXRay aria-hidden="true" /> {imaging.generation_status} · {imaging.report_status}</span>
                      <Link className="nhsuk-button nhsuk-button--secondary" to={`/intranet/ecamis/imaging/${imaging.id}`}>Open imaging study</Link>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "messages" && (
        <div className="pguh-clinical-two-column">
          <form className="pguh-clinical-form" onSubmit={sendPatientMessage}>
            <h3 className="nhsuk-heading-s">Send portal message</h3>
            {!portalAccountId && <div className="nhsuk-warning-callout"><p>This patient is not linked to a patient portal account.</p></div>}
            <label className="nhsuk-label" htmlFor="patient-message-category">Category</label>
            <select id="patient-message-category" className="nhsuk-select" value={messageDraft.category} onChange={(event) => setMessageDraft((current) => ({ ...current, category: event.target.value }))}>
              {["General", "Appointment", "Clinical update", "Discharge", "Results"].map((item) => <option key={item}>{item}</option>)}
            </select>
            <label className="nhsuk-label" htmlFor="patient-message-subject">Subject</label>
            <input id="patient-message-subject" className="nhsuk-input" value={messageDraft.subject} onChange={(event) => setMessageDraft((current) => ({ ...current, subject: event.target.value }))} required />
            <label className="nhsuk-label" htmlFor="patient-message-body">Message</label>
            <textarea id="patient-message-body" className="nhsuk-textarea" rows={6} value={messageDraft.body} onChange={(event) => setMessageDraft((current) => ({ ...current, body: event.target.value }))} required />
            <button className="nhsuk-button" disabled={working || !portalAccountId}>Send to patient inbox</button>
          </form>

          <div className="pguh-clinical-list">
            <h3 className="nhsuk-heading-s">Messages sent</h3>
            {messages.length === 0 ? <p>No portal messages sent.</p> : messages.map((row) => (
              <article key={row.id}>
                <div><strong>{row.subject}</strong><span>{row.read_at ? "Read" : "Unread"}</span></div>
                <p>{row.body}</p>
                <small>{row.category} · {formatDateTime(row.created_at)}</small>
              </article>
            ))}
          </div>
        </div>
      )}

      {activeTab === "discharge" && (
        <div className="pguh-clinical-two-column">
          <form className="pguh-clinical-form" onSubmit={createDischargeSummary}>
            <h3 className="nhsuk-heading-s">Discharge patient</h3>
            <div className="nhsuk-warning-callout"><p>Saving a discharge summary archives the current attendance and releases any assigned bed.</p></div>
            <label className="nhsuk-label" htmlFor="discharge-diagnosis">Diagnosis / outcome</label>
            <textarea id="discharge-diagnosis" className="nhsuk-textarea" rows={3} value={dischargeDraft.diagnosis} onChange={(event) => setDischargeDraft((current) => ({ ...current, diagnosis: event.target.value }))} required />
            <label className="nhsuk-label" htmlFor="discharge-treatment">Treatment</label>
            <textarea id="discharge-treatment" className="nhsuk-textarea" rows={3} value={dischargeDraft.treatment} onChange={(event) => setDischargeDraft((current) => ({ ...current, treatment: event.target.value }))} />
            <label className="nhsuk-label" htmlFor="discharge-medications">Medication on discharge</label>
            <textarea id="discharge-medications" className="nhsuk-textarea" rows={2} value={dischargeDraft.medications} onChange={(event) => setDischargeDraft((current) => ({ ...current, medications: event.target.value }))} />
            <label className="nhsuk-label" htmlFor="discharge-advice">Discharge advice</label>
            <textarea id="discharge-advice" className="nhsuk-textarea" rows={3} value={dischargeDraft.discharge_advice} onChange={(event) => setDischargeDraft((current) => ({ ...current, discharge_advice: event.target.value }))} />
            <label className="nhsuk-label" htmlFor="discharge-follow-up">Follow-up</label>
            <textarea id="discharge-follow-up" className="nhsuk-textarea" rows={3} value={dischargeDraft.follow_up} onChange={(event) => setDischargeDraft((current) => ({ ...current, follow_up: event.target.value }))} />
            <button className="nhsuk-button nhsuk-button--danger" disabled={working}>Save discharge summary</button>
          </form>

          <div className="pguh-clinical-list">
            <h3 className="nhsuk-heading-s">Previous discharge summaries</h3>
            {discharges.length === 0 ? <p>No discharge summaries recorded.</p> : discharges.map((row) => (
              <article key={row.id}>
                <div><strong>{row.diagnosis}</strong><span>{formatDateTime(row.discharged_at)}</span></div>
                {row.treatment && <p><strong>Treatment:</strong> {row.treatment}</p>}
                {row.discharge_advice && <p><strong>Advice:</strong> {row.discharge_advice}</p>}
                {row.follow_up && <p><strong>Follow-up:</strong> {row.follow_up}</p>}
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
